/**
 * Social Studio -> Drafts. The screen the dashboard row has been pointing at.
 *
 * The NEEDS YOUR ATTENTION row deep-links to `?module=social&tab=drafts` and has done since
 * 2026-08-27. This tab is that destination; before it existed the row counted four posts
 * awaiting review and led nowhere.
 *
 * WHAT THIS SCREEN CAN AND CANNOT DO
 * ----------------------------------
 * It reviews. Approving records Randall's OK against a digest of what he was shown and stops
 * there - it does not schedule, queue or post, and the banner says so, because a button whose
 * consequences the reader has to guess is how "approve" quietly becomes "publish".
 *
 * THE PICTURE IS THE POINT. An image is approved by looking at it, never from a description
 * of it, so the artwork renders full-width above the copy. Where we hold artwork we cannot
 * display, the card says exactly that and the approve button is gone - the backend refuses
 * the same case, so this is the honest UI over a real guard rather than the guard itself.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Check, Undo2, Loader2, AlertTriangle, ImageOff, Clock, RefreshCw, Paperclip, Film, X, CalendarClock, Ban, Lock, PlugZap, Music, LibraryBig } from 'lucide-react';
import { MediaLibraryList } from './MediaLibraryList';
import { DATE_FLAG_BADGE } from './socialDateFlags';
import SocialAdPanel from './SocialAdPanel';

const API = '/api';

const STATE_PILL = {
  needs_review: 'border-amber-400 text-amber-300',
  needs_asset: 'border-blue-400 text-blue-300',
  approved: 'border-hg-green text-hg-green',
  held: 'border-hg-border text-hg-text-dim',
  scheduled: 'border-hg-border text-hg-text-dim',
  published: 'border-hg-border text-hg-text-dim',
  skipped: 'border-hg-border text-hg-text-dim',
  pushed_paused: 'border-[#B4531F] text-[#B4531F]',
  ad_active: 'border-[#1F6F2A] text-[#1F6F2A]',
  ad_ended: 'border-hg-border text-hg-text-dim',
};

const STATE_LABEL = {
  needs_review: 'Needs review',
  needs_asset: 'Needs an image',
  approved: 'Approved',
  held: 'Held',
  scheduled: 'Scheduled',
  published: 'Published',
  skipped: 'Skipped',
  // Meta ads (18 Sep 2026)
  pushed_paused: 'At Meta — paused',
  ad_active: 'Ad running',
  ad_ended: 'Ad ended',
};

/** "Tue 15 Sep" for the plan's slot. Parsed as a calendar day, never as a UTC instant — a
 *  date-only string read as midnight UTC shows the day before in Pacific. */
function slotLabel(iso) {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

/** "in 4 days" / "3 days ago" / "No date set" — said in words, because a bare ISO string
 *  makes the reader do the arithmetic that decides whether this is urgent. */
function whenText(d) {
  if (d.days_until === null || d.days_until === undefined) return 'No date set';
  if (d.days_until < 0) return `${Math.abs(d.days_until)} day${Math.abs(d.days_until) === 1 ? '' : 's'} overdue`;
  if (d.days_until === 0) return 'Today';
  return `In ${d.days_until} day${d.days_until === 1 ? '' : 's'}`;
}

/**
 * The three things Randall must SAY about a file before it can become artwork.
 *
 * Every one starts UNSET and the submit stays disabled until all three are answered. That is
 * not politeness — the backend refuses a missing declaration outright, and a UI that
 * pre-selected "No" would be quietly answering on his behalf. A defaulted "no shop CTA" is
 * indistinguishable from a human having looked at the file and said so, and only one of those
 * is worth anything.
 *
 * Asked in the words the drafts use ("watermark-free", "without old logo"); the backend maps
 * them onto the library's negative flag names.
 */
const DECLARATIONS = [
  { key: 'contains_shop_cta', q: 'Does the artwork have SHOP NOW printed into it?', yes: 'Yes', no: 'No' },
  { key: 'watermark_free', q: 'Is it watermark-free?', yes: 'Yes', no: 'No' },
  { key: 'old_logo_free', q: 'Is it free of the old logo?', yes: 'Yes', no: 'No' },
];

function Declarations({ value, onChange }) {
  return (
    <div className="space-y-2">
      {DECLARATIONS.map(({ key, q, yes, no }) => (
        <div key={key} className="flex items-center justify-between gap-3 flex-wrap">
          <span className="text-xs text-hg-text font-sans">{q}</span>
          <div className="flex gap-1">
            {[['yes', true], ['no', false]].map(([label, v]) => (
              <button
                key={label}
                type="button"
                data-testid={`decl-${key}-${label}`}
                onClick={() => onChange({ ...value, [key]: v })}
                className={`text-[11px] font-sans uppercase tracking-wider px-3 py-1 rounded-sm border ${
                  value[key] === v ? 'border-hg-green text-hg-green' : 'border-hg-border text-hg-text-dim hover:text-hg-text'
                }`}
              >
                {v ? yes : no}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function AttachPanel({ draft, onAttached, onClose }) {
  const [decl, setDecl] = useState({});
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [clips, setClips] = useState(null);      // null = not asked, [] = asked and none
  const [clipsErr, setClipsErr] = useState('');
  const [projectId, setProjectId] = useState('');

  const declared = DECLARATIONS.every(({ key }) => typeof decl[key] === 'boolean');

  // --- Insert from the HG media library (use=social). Everything here comes from the feed,
  // so it is already approved — which is why no declarations are asked for a library pick.
  const [lib, setLib] = useState(null);          // null = not asked, [] = asked and none
  const [libErr, setLibErr] = useState('');
  const isInstagram = (draft.services || []).map((s) => String(s).toLowerCase()).includes('instagram');

  const loadLibrary = async () => {
    setLibErr('');
    setLib(null);
    try {
      const res = await axios.get(`${API}/social/drafts/media-library`);
      setLib(res.data.assets || []);
      // "The library is down" and "the library has nothing for social" are different facts.
      if (res.data.error) setLibErr(res.data.error);
    } catch (e) {
      setLibErr(e?.response?.data?.detail || 'Could not read the media library. This is a failed request, not an empty library.');
    }
  };

  const attachMedia = async (m) => {
    setBusy(true); setError('');
    try {
      const res = await axios.post(`${API}/social/drafts/${draft.id}/attach-media`, { media_id: m.id });
      onAttached(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not insert that from the library.');
    }
    setBusy(false);
  };

  // The same two rules the server enforces, said in words beside the item rather than hidden.
  const libraryBlock = (m) => {
    if (m.kind === 'audio' && !isInstagram) return 'A library sound is a Reel’s sound — this draft does not post to Instagram.';
    if (m.kind === 'film' && isInstagram && m.orientation !== 'vertical') return 'Instagram takes a film only as its vertical cut — the Hub does not crop video.';
    return null;
  };
  const libraryLabel = (m) => (m.kind === 'audio' ? 'Use as Reel sound' : m.kind === 'film' ? 'Use this film' : 'Use this');

  const loadClips = async () => {
    setClipsErr('');
    setClips(null);
    try {
      let pid = projectId;
      if (!pid) {
        const pr = await axios.get(`${API}/social/opus/projects`);
        pid = pr.data.default_project_id || (pr.data.projects?.[0]?.project_id ?? '');
        setProjectId(pid);
        if (!pid) { setClipsErr('OpusClip returned no projects for this account.'); return; }
      }
      const res = await axios.get(`${API}/social/opus/clips`, { params: { project_id: pid } });
      setClips(res.data.clips || []);
    } catch (e) {
      // "We could not ask" is not "there are none". Only one of them means go and upload.
      setClipsErr(e?.response?.data?.detail || 'Could not reach OpusClip. This is a failed request, not an empty project.');
    }
  };

  const attachFile = async () => {
    if (!file || !declared) return;
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      DECLARATIONS.forEach(({ key }) => fd.append(key, String(decl[key])));
      const res = await axios.post(`${API}/social/drafts/${draft.id}/attach`, fd);
      onAttached(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not attach that file.');
    }
    setBusy(false);
  };

  const attachClip = async (clip) => {
    if (!declared) return;
    setBusy(true); setError('');
    try {
      const res = await axios.post(`${API}/social/drafts/${draft.id}/attach-opus`, {
        project_id: clip.project_id || projectId,
        clip_id: clip.clip_id,
        ...DECLARATIONS.reduce((a, { key }) => ({ ...a, [key]: decl[key] }), {}),
      });
      onAttached(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not attach that clip.');
    }
    setBusy(false);
  };

  return (
    <div data-testid={`attach-panel-${draft.id}`} className="border border-hg-border rounded-sm p-4 space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-sans uppercase tracking-wider text-hg-text">Attach artwork</span>
        <button onClick={onClose} className="text-hg-text-dim hover:text-hg-text"><X size={14} /></button>
      </div>

      {draft.asset_wanted && (
        <p className="text-xs text-hg-text-dim font-sans">This draft asks for: {draft.asset_wanted}</p>
      )}

      <div className="space-y-2" data-testid={`library-section-${draft.id}`}>
        <button
          data-testid={`library-browse-${draft.id}`}
          onClick={loadLibrary}
          disabled={busy}
          className="flex items-center gap-1.5 border border-hg-border text-hg-text-dim hover:text-hg-text text-xs font-sans uppercase tracking-wider px-4 py-2 rounded-sm disabled:opacity-40"
        >
          <LibraryBig size={13} strokeWidth={1.5} />
          Insert from media library
        </button>
        <p className="text-[11px] text-hg-text-dim font-sans">
          HG’s approved films, stills and sound. They were approved into the library, so nothing
          here asks for declarations — and nothing is uploaded again.
        </p>
        {libErr && (
          <div data-testid={`library-error-${draft.id}`} className="border border-red-400 rounded-sm p-2 text-xs text-red-700 font-sans">{libErr}</div>
        )}
        {lib && lib.length === 0 && !libErr && (
          <p className="text-xs text-hg-text-dim font-sans">The library has nothing tagged for social yet.</p>
        )}
        {lib && lib.length > 0 && (
          <MediaLibraryList
            items={lib}
            testIdPrefix={`library-${draft.id}`}
            title="HG media library — social"
            busy={busy}
            onPick={attachMedia}
            pickLabel={libraryLabel}
            blockReason={libraryBlock}
          />
        )}
      </div>

      <div className="pt-3 border-t border-hg-border text-[11px] text-hg-text-dim font-sans uppercase tracking-wider">
        Or attach a file
      </div>

      <Declarations value={decl} onChange={setDecl} />
      {!declared && (
        <p className="text-[11px] text-hg-text-dim font-sans">
          All three have to be answered before anything can be attached — they are recorded as your
          declaration about the file, and nothing infers them from it.
        </p>
      )}

      {error && <div data-testid={`attach-error-${draft.id}`} className="border border-red-400 rounded-sm p-2 text-xs text-red-300 font-sans">{error}</div>}

      <div className="space-y-2">
        <input
          data-testid={`attach-file-${draft.id}`}
          type="file"
          accept="image/*,video/*"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="block w-full text-xs text-hg-text-dim font-sans file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border file:border-hg-border file:bg-transparent file:text-hg-text file:text-xs"
        />
        <button
          data-testid={`attach-submit-${draft.id}`}
          disabled={!file || !declared || busy}
          onClick={attachFile}
          className="flex items-center gap-1.5 bg-hg-green text-hg-bg text-xs font-sans uppercase tracking-wider px-4 py-2 rounded-sm disabled:opacity-40"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Paperclip size={13} />}
          Attach this file
        </button>
      </div>

      {/* Only offered where there is an unmet want — an Opus pull replaces missing artwork. */}
      {!draft.image?.has_asset && (
        <div className="pt-3 border-t border-hg-border space-y-2">
          <button
            data-testid={`opus-browse-${draft.id}`}
            onClick={loadClips}
            className="flex items-center gap-1.5 border border-hg-border text-hg-text-dim hover:text-hg-text text-xs font-sans uppercase tracking-wider px-4 py-2 rounded-sm"
          >
            <Film size={13} strokeWidth={1.5} />
            Attach from OpusClip
          </button>
          {clipsErr && (
            <div data-testid={`opus-error-${draft.id}`} className="border border-red-400 rounded-sm p-2 text-xs text-red-300 font-sans">{clipsErr}</div>
          )}
          {clips && clips.length === 0 && !clipsErr && (
            <p className="text-xs text-hg-text-dim font-sans">That OpusClip project has no clips.</p>
          )}
          {clips && clips.length > 0 && (
            <ul className="space-y-2">
              {clips.map((c) => (
                <li key={c.clip_full_id} className="flex items-center gap-3 border border-hg-border rounded-sm p-2">
                  {c.thumbnail_url && <img src={c.thumbnail_url} alt="" className="w-16 h-16 object-cover rounded-sm" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-hg-text font-sans truncate">{c.title || c.clip_id}</p>
                    <p className="text-[11px] text-hg-text-dim font-sans">
                      {c.duration_s ? `${c.duration_s}s` : ''}{c.render_pending ? ' · still rendering in Opus' : ''}
                    </p>
                  </div>
                  <button
                    data-testid={`opus-pick-${c.clip_id}`}
                    disabled={!declared || busy || c.render_pending}
                    onClick={() => attachClip(c)}
                    className="text-[11px] font-sans uppercase tracking-wider border border-hg-border text-hg-text px-3 py-1.5 rounded-sm disabled:opacity-40"
                  >
                    Use this
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[11px] text-hg-text-dim font-sans">
            The Hub only reads from OpusClip. Headlines, crops and re-renders stay in Opus — if a
            clip needs a different headline, re-render it there and attach it again here.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Approved -> a real post in Buffer's queue. The one step the review surface deliberately
 * lacked, and the only place in the Hub that puts anything on a channel.
 *
 * THREE SCREENS, IN THIS ORDER, and the order is the point:
 *   1. pick a channel  - every channel is shown, with the guard's verdict on it. A channel
 *                        that cannot receive a post is visible and disabled with the reason,
 *                        never hidden and never a dead button. "Locked on the plan" and
 *                        "connection dropped" send Randall to two different screens.
 *   2. pick a time     - Pacific, and the server checks it is still to come.
 *   3. CONFIRM         - the full final post: the copy, the artwork playing, the channel and
 *                        the exact local date and time. Confirming THAT screen is what mints
 *                        the send approval, and the digest it is keyed to comes back with the
 *                        preview so the server can refuse a confirm for a version he was not
 *                        shown.
 *
 * The review approval covered copy and image. A channel and a time are new content under the
 * same digest contract, so this asks him again with the real date in front of him.
 */
function ChannelRow({ channel, selected, onSelect }) {
  const Icon = channel.post_state === 'locked_on_plan' ? Lock
    : channel.postable ? Check : PlugZap;
  return (
    <button
      type="button"
      data-testid={`sched-channel-${channel.id}`}
      disabled={!channel.postable}
      onClick={() => onSelect(channel)}
      className={`w-full text-left flex items-start gap-3 border rounded-sm p-3 ${
        selected ? 'border-hg-green' : 'border-hg-border'
      } ${channel.postable ? 'hover:border-hg-green' : 'opacity-70 cursor-not-allowed'}`}
    >
      <Icon size={14} strokeWidth={1.5}
            className={`shrink-0 mt-0.5 ${channel.postable ? 'text-hg-green' : 'text-amber-300'}`} />
      <div className="min-w-0">
        <p className="text-xs text-hg-text font-sans">{channel.name} <span className="text-hg-text-dim">· {channel.service}</span></p>
        {!channel.postable && (
          <p className="text-[11px] text-amber-300 font-sans mt-0.5">
            {channel.post_state === 'locked_on_plan'
              ? 'Locked on your Buffer plan — this needs a Buffer plan change, not a reconnect.'
              : channel.block_reason}
          </p>
        )}
      </div>
    </button>
  );
}

function SchedulePanel({ draft, onScheduled, onClose }) {
  const [options, setOptions] = useState(null);
  const [channel, setChannel] = useState(null);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/social/drafts/${draft.id}/schedule-options`);
        if (!dead) setOptions(r.data);
      } catch (e) {
        if (!dead) setError(e?.response?.data?.detail || 'Could not read the channel list from Buffer.');
      }
    })();
    return () => { dead = true; };
  }, [draft.id]);

  const loadPreview = async () => {
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${API}/social/drafts/${draft.id}/schedule-preview`, {
        channel_id: channel.id, date, time,
      });
      setPreview(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not prepare that schedule.');
    }
    setBusy(false);
  };

  const confirm = async () => {
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${API}/social/drafts/${draft.id}/schedule`, {
        channel_id: channel.id, date, time, confirm_hash: preview.confirm_hash,
      });
      onScheduled(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not schedule that post.');
    }
    setBusy(false);
  };

  // --- the confirm screen: everything, exactly as it will go out ---
  if (preview) {
    const img = preview.image || {};
    return (
      <div data-testid={`confirm-panel-${draft.id}`} className="border border-hg-green rounded-sm p-4 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-sans uppercase tracking-wider text-hg-green">Confirm this post</span>
          <button onClick={() => setPreview(null)} className="text-hg-text-dim hover:text-hg-text"><X size={14} /></button>
        </div>

        {img.url && img.kind === 'video' && (
          <video data-testid={`confirm-video-${draft.id}`} src={img.url} poster={img.poster || undefined}
                 controls muted playsInline preload="metadata"
                 className="w-full max-h-[360px] bg-hg-bg border border-hg-border rounded-sm" />
        )}
        {img.url && img.kind !== 'video' && (
          <img data-testid={`confirm-image-${draft.id}`} src={img.url} alt=""
               className="w-full max-h-[360px] object-contain bg-hg-bg border border-hg-border rounded-sm" />
        )}

        <p className="text-sm text-hg-text font-sans whitespace-pre-wrap leading-relaxed">{preview.copy}</p>

        {(preview.warnings || []).map((w, i) => (
          <p key={i} className="flex gap-2 items-start text-xs text-amber-300 font-sans">
            <AlertTriangle size={13} strokeWidth={1.5} className="shrink-0 mt-0.5" />{w}
          </p>
        ))}

        <dl className="text-xs font-sans border-t border-hg-border pt-3 space-y-1">
          <div className="flex justify-between gap-3">
            <dt className="text-hg-text-dim">Channel</dt>
            <dd className="text-hg-text text-right">{preview.channel.name} · {preview.channel.service}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-hg-text-dim">Goes out</dt>
            <dd data-testid={`confirm-when-${draft.id}`} className="text-hg-text text-right">{preview.local.display}</dd>
          </div>
        </dl>

        {error && <div data-testid={`sched-error-${draft.id}`} className="border border-red-400 rounded-sm p-2 text-xs text-red-300 font-sans">{error}</div>}

        <p className="text-[11px] text-hg-text-dim font-sans">
          Confirming records your OK for this exact copy, image, channel and time. Change any of
          them afterwards and the approval is void and the send refuses.
        </p>
        <button
          data-testid={`confirm-schedule-${draft.id}`}
          disabled={busy}
          onClick={confirm}
          className="flex items-center gap-1.5 bg-hg-green text-hg-bg text-xs font-sans uppercase tracking-wider px-4 py-2 rounded-sm disabled:opacity-40"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <CalendarClock size={13} />}
          Schedule this post
        </button>
      </div>
    );
  }

  // --- pick a channel and a time ---
  return (
    <div data-testid={`schedule-panel-${draft.id}`} className="border border-hg-border rounded-sm p-4 space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-sans uppercase tracking-wider text-hg-text">Schedule</span>
        <button onClick={onClose} className="text-hg-text-dim hover:text-hg-text"><X size={14} /></button>
      </div>

      {error && <div data-testid={`sched-error-${draft.id}`} className="border border-red-400 rounded-sm p-2 text-xs text-red-300 font-sans">{error}</div>}
      {!options && !error && <p className="text-xs text-hg-text-dim font-sans">Reading your Buffer channels…</p>}

      {options && (
        <>
          <div className="space-y-2">
            {options.channels.map((c) => (
              <ChannelRow key={c.id} channel={c} selected={channel?.id === c.id} onSelect={setChannel} />
            ))}
          </div>
          {options.postable_count === 0 && (
            <p className="text-xs text-amber-300 font-sans">
              None of your Buffer channels can take a post right now. Nothing here can be scheduled
              until a channel is reconnected or the plan has a free slot.
            </p>
          )}
          <div className="flex gap-2 flex-wrap items-end">
            <label className="text-[11px] text-hg-text-dim font-sans">
              Date
              <input data-testid={`sched-date-${draft.id}`} type="date" value={date}
                     onChange={(e) => setDate(e.target.value)}
                     className="block bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-xs text-hg-text font-sans" />
            </label>
            <label className="text-[11px] text-hg-text-dim font-sans">
              Time ({options.timezone})
              <input data-testid={`sched-time-${draft.id}`} type="time" value={time}
                     onChange={(e) => setTime(e.target.value)}
                     className="block bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-xs text-hg-text font-sans" />
            </label>
            <button
              data-testid={`sched-review-${draft.id}`}
              disabled={!channel || !date || !time || busy}
              onClick={loadPreview}
              className="border border-hg-border text-hg-text text-xs font-sans uppercase tracking-wider px-4 py-2 rounded-sm disabled:opacity-40"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : 'Review before scheduling'}
            </button>
          </div>
          <p className="text-[11px] text-hg-text-dim font-sans">
            Nothing is scheduled until you confirm on the next screen.
          </p>
        </>
      )}
    </div>
  );
}

/**
 * "Approve and schedule to Buffer" — Randall, 10 Sep 2026. One click from the review queue, and
 * still the full confirm: the artwork, the caption exactly as EACH channel will post it, the
 * page and the time. Nothing is approved or sent until he confirms this screen, and the server
 * refuses a confirm for anything other than what it showed. A draft for Facebook and X becomes
 * one post per channel.
 */
function OneClickPanel({ draft, onDone, onClose }) {
  const [plan, setPlan] = useState(null);
  const [time, setTime] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (t) => {
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${API}/social/drafts/${draft.id}/approve-and-schedule-preview`, t ? { time: t } : {});
      setPlan(r.data);
      setTime(r.data.local.time);
    } catch (e) {
      setPlan(null);
      setError(e?.response?.data?.detail || 'Could not prepare this post for Buffer.');
    }
    setBusy(false);
  }, [draft.id]);

  useEffect(() => { load(''); }, [load]);

  const confirm = async () => {
    setBusy(true); setError('');
    try {
      const map = Object.fromEntries(plan.targets.map((t) => [t.service, t.confirm_hash]));
      const r = await axios.post(`${API}/social/drafts/${draft.id}/approve-and-schedule`, {
        time: plan.local.time, confirm: map,
      });
      onDone(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not approve and schedule this post.');
    }
    setBusy(false);
  };

  const img = plan?.image || {};
  const n = plan?.targets?.length || 0;
  return (
    <div data-testid={`oneclick-panel-${draft.id}`} className="border border-hg-green rounded-sm p-4 space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-sans uppercase tracking-wider text-hg-green">
          {plan && !plan.approves ? 'Confirm and schedule to Buffer' : 'Approve and schedule to Buffer'}
        </span>
        <button onClick={onClose} className="text-hg-text-dim hover:text-hg-text"><X size={14} /></button>
      </div>
      {!plan && !error && <p className="text-xs text-hg-text-dim font-sans">Reading your Buffer channels…</p>}
      {error && <div data-testid={`oneclick-error-${draft.id}`} className="border border-red-400 rounded-sm p-2 text-xs text-red-300 font-sans">{error}</div>}

      {plan && (
        <>
          {img.url && img.kind === 'video' && (
            <video src={img.url} poster={img.poster || undefined} controls muted playsInline preload="metadata"
                   className="w-full max-h-[360px] bg-hg-bg border border-hg-border rounded-sm" />
          )}
          {img.url && img.kind !== 'video' && (
            <img src={img.url} alt="" className="w-full max-h-[360px] object-contain bg-hg-bg border border-hg-border rounded-sm" />
          )}
          {plan.targets.map((t) => (
            <div key={t.service} data-testid={`oneclick-target-${draft.id}-${t.service}`} className="border border-hg-border rounded-sm p-3 space-y-2">
              <div className="text-[11px] font-sans uppercase tracking-wider text-hg-text-dim">
                {t.channel.name} · {t.channel.service}
                {t.draft_id !== draft.id && <span className="normal-case tracking-normal"> — its own post ({t.draft_id})</span>}
              </div>
              <p className="text-sm text-hg-text font-sans whitespace-pre-wrap leading-relaxed break-words">{t.copy}</p>
            </div>
          ))}
          {(plan.warnings || []).map((w, i) => (
            <p key={i} className="flex gap-2 items-start text-xs text-amber-300 font-sans">
              <AlertTriangle size={13} strokeWidth={1.5} className="shrink-0 mt-0.5" />{w}
            </p>
          ))}
          <dl className="text-xs font-sans border-t border-hg-border pt-3 space-y-1">
            <div className="flex justify-between gap-3">
              <dt className="text-hg-text-dim">Goes out</dt>
              <dd data-testid={`oneclick-when-${draft.id}`} className="text-hg-text text-right">
                {plan.local.display}
                {plan.local.timezone !== 'America/Los_Angeles' && <span className="block text-hg-text-dim">{plan.local.pacific}</span>}
              </dd>
            </div>
          </dl>
          <div className="flex gap-2 items-end flex-wrap">
            <label className="text-[11px] text-hg-text-dim font-sans">
              Time ({plan.local.timezone})
              <input data-testid={`oneclick-time-${draft.id}`} type="time" value={time}
                     onChange={(e) => setTime(e.target.value)}
                     className="block bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-xs text-hg-text font-sans" />
            </label>
            {time && time !== plan.local.time && (
              <button disabled={busy} onClick={() => load(time)}
                      className="border border-hg-border text-hg-text text-xs font-sans uppercase tracking-wider px-3 py-2 rounded-sm disabled:opacity-40">
                Change time
              </button>
            )}
          </div>
          <p className="text-[11px] text-hg-text-dim font-sans">
            Confirming {plan.approves ? 'records your approval of this copy and artwork, and ' : ''}your OK
            to send {n === 1 ? 'it' : `these ${n} posts`} to {n === 1 ? 'this channel' : 'these channels'} at this time.
            Change any of them afterwards and the approval is void and the send refuses.
          </p>
          <button
            data-testid={`oneclick-confirm-${draft.id}`}
            disabled={busy || (time && time !== plan.local.time)}
            onClick={confirm}
            className="flex items-center gap-1.5 bg-hg-green text-hg-bg text-xs font-sans uppercase tracking-wider px-4 py-2 rounded-sm disabled:opacity-40"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <CalendarClock size={13} />}
            {plan.approves ? 'Approve and schedule' : 'Schedule'} {n === 1 ? 'this post' : `${n} posts`}
          </button>
        </>
      )}
    </div>
  );
}

function DraftCard({ draft, onApprove, onReturn, onAttached, onScheduled, onOneClick, busy, focused = false }) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [schedOpen, setSchedOpen] = useState(false);
  const [oneClickOpen, setOneClickOpen] = useState(false);
  const [note, setNote] = useState('');
  const img = draft.image || {};
  const approval = draft.approval;
  const isAd = draft.distribution === 'ad';

  return (
    <div data-testid={`draft-card-${draft.id}`} id={`draft-${draft.id}`}
         className={`bg-hg-surface border rounded-sm p-5 space-y-4 ${focused ? 'border-hg-green ring-2 ring-hg-green' : 'border-hg-border'}`}>
      {/* --- header: state, what he has to do, when it bites --- */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] font-sans uppercase tracking-wider px-2 py-0.5 rounded-sm border ${STATE_PILL[draft.state] || STATE_PILL.held}`}>
            {STATE_LABEL[draft.state] || draft.state}
          </span>
          {draft.asks && <span className="text-xs text-hg-text-dim font-sans">{draft.asks}</span>}
          {draft.awaiting_vertical && (
            // The Instagram hold, visible on the card itself — the approve button is gone and
            // this says why before he goes looking for it.
            <span data-testid={`draft-awaiting-vertical-${draft.id}`}
                  className="text-[10px] font-sans uppercase tracking-wider px-2 py-0.5 rounded-sm border border-[#b7791f] text-[#8a5a00]">
              Waiting for vertical cut
            </span>
          )}
          {isAd && (
            <span data-testid={`draft-ad-badge-${draft.id}`}
                  className="text-[10px] font-sans uppercase tracking-wider px-2 py-0.5 rounded-sm border border-[#B4531F] text-[#B4531F]">
              Meta ad{draft.ad ? ` · ${draft.ad.start_date} → ${draft.ad.end_date}` : ''}
            </span>
          )}
          {draft.campaign_id && (
            <span className="text-[10px] text-hg-text-dim font-sans uppercase tracking-wider">
              {draft.campaign_id}{draft.slot ? ` · ${draft.slot}` : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-hg-text-dim font-sans">
          {draft.scheduled_for && (
            <span data-testid={`draft-slot-${draft.id}`} className="text-hg-text">{slotLabel(draft.scheduled_for)}</span>
          )}
          {/* The date guard (11 Sep 2026): a warning, never a block. Opaque colours - light theme. */}
          {DATE_FLAG_BADGE[draft.date_flag] && (
            <span data-testid={`draft-date-flag-${draft.id}`}
                  className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider"
                  style={{ background: DATE_FLAG_BADGE[draft.date_flag].bg, color: DATE_FLAG_BADGE[draft.date_flag].fg }}>
              {DATE_FLAG_BADGE[draft.date_flag].label}
            </span>
          )}
          <span className="flex items-center gap-1"><Clock size={12} strokeWidth={1.5} />{whenText(draft)}</span>
          {draft.age_days > 0 && <span>drafted {draft.age_days}d ago</span>}
        </div>
      </div>

      {/* --- the artwork. Approving from a description of an image is not approving it. --- */}
      {img.url && img.kind === 'video' && (
        // Muted and inline so it can autoplay-on-tap on iOS without hijacking the page, with
        // controls because reviewing means scrubbing. The poster is Cloudinary's still of this
        // same stored object, so it cannot show a frame from a different file.
        <video
          data-testid={`draft-video-${draft.id}`}
          src={img.url}
          poster={img.poster || undefined}
          controls muted playsInline preload="metadata"
          className="w-full max-h-[420px] bg-hg-bg border border-hg-border rounded-sm"
        />
      )}
      {img.url && img.kind !== 'video' && (
        <img src={img.url} alt={img.filename || 'Draft artwork'}
             data-testid={`draft-image-${draft.id}`}
             className="w-full max-h-[420px] object-contain bg-hg-bg border border-hg-border rounded-sm" />
      )}
      {/* We hold the artwork and cannot show it. NOT the same as having none: he must not be
          asked to supply a picture he already gave us. */}
      {!img.url && img.preview_error && (
        <div data-testid={`draft-preview-error-${draft.id}`}
             className="flex gap-2 items-start border border-red-400 rounded-sm p-3 text-xs text-red-300 font-sans">
          <AlertTriangle size={14} strokeWidth={1.5} className="shrink-0 mt-0.5" />
          <span>{img.preview_error}</span>
        </div>
      )}
      {/* Genuinely no artwork yet — a real state, answered by supplying a file. */}
      {!img.url && !img.preview_error && !img.has_asset && (
        <div data-testid={`draft-no-image-${draft.id}`}
             className="flex gap-2 items-center border border-dashed border-hg-border rounded-sm p-4 text-xs text-hg-text-dim font-sans">
          <ImageOff size={14} strokeWidth={1.5} className="shrink-0" />
          <span>{draft.asset_wanted ? `No artwork yet — needs ${draft.asset_wanted}` : 'No artwork yet.'}</span>
        </div>
      )}

      {/* --- a Reel's sound, from the media library. Part of what the approval covers. --- */}
      {draft.sound && (
        <div data-testid={`draft-sound-${draft.id}`} className="flex items-center gap-3 border border-hg-border rounded-sm p-3">
          <Music size={14} strokeWidth={1.5} className="shrink-0 text-hg-text-dim" />
          <div className="min-w-0 flex-1">
            <div className="text-xs text-hg-text font-sans truncate">Reel sound: {draft.sound.title}</div>
            <div className="text-[11px] text-hg-text-dim font-sans">
              {draft.sound.duration ? `${Math.round(draft.sound.duration)}s · ` : ''}from the media library · covered by your approval
            </div>
          </div>
          <audio controls preload="none" src={draft.sound.url} className="h-8 w-44 shrink-0" />
        </div>
      )}

      {/* --- a Meta AD: the ad as it would run, its terms, and the push --- */}
      {isAd && <SocialAdPanel draft={draft} onChanged={onAttached} busy={busy} />}

      {/* --- the copy, exactly as it would post --- */}
      {!isAd && draft.caption_x && (
        <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">
          {(draft.services || []).some((s) => s !== 'x')
            ? `${(draft.services || []).filter((s) => s !== 'x').join(' / ')} caption`
            : 'Shared caption — X posts its own, below'}
        </div>
      )}
      {!isAd && (
        <p data-testid={`draft-copy-${draft.id}`} className="text-sm text-hg-text font-sans whitespace-pre-wrap leading-relaxed break-words">{draft.rendered_copy || draft.copy}</p>
      )}
      {/* X's side: its own caption when the shared one runs over X's 280, and X's count of
          what it would post — a link counts 23, arrows and emoji 2. */}
      {!isAd && draft.x_caption !== null && draft.x_caption !== undefined && (
        <div data-testid={`draft-x-caption-${draft.id}`} className="border border-hg-border rounded-sm p-3 space-y-1.5">
          <div className="flex items-center justify-between gap-2 text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">
            <span>{draft.caption_x ? 'X caption' : 'X posts the caption above'}</span>
            <span data-testid={`draft-x-count-${draft.id}`}
                  className={draft.x_count > draft.x_limit ? 'text-[#b42318] font-bold' : 'text-hg-text'}>
              {draft.x_count}/{draft.x_limit} by X's count
            </span>
          </div>
          {draft.caption_x && (
            <p className="text-sm text-hg-text font-sans whitespace-pre-wrap leading-relaxed break-words">{draft.x_caption}</p>
          )}
        </div>
      )}
      {/* Instagram's side (link in bio, 22 Sep 2026): Instagram cannot click a link, so its CTA
          posts as words — shown whenever it differs from the caption above. */}
      {!isAd && draft.instagram_caption && draft.instagram_caption !== (draft.rendered_copy || draft.copy) && (
        <div data-testid={`draft-ig-caption-${draft.id}`} className="border border-hg-border rounded-sm p-3 space-y-1.5">
          <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">
            Instagram posts — link in bio, no URL
          </div>
          <p className="text-sm text-hg-text font-sans whitespace-pre-wrap leading-relaxed break-words">{draft.instagram_caption}</p>
        </div>
      )}
      {(draft.copy || '').includes('[locator link]') && (
        <p className="text-[11px] text-hg-text-dim font-sans">
          The find-a-fitter link is the Hub’s counted link — each channel gets its own when it posts.
        </p>
      )}
      {draft.clicks !== null && draft.clicks !== undefined && (
        <div data-testid={`draft-clicks-${draft.id}`} className="text-xs text-hg-text-dim font-sans">
          Find-a-fitter clicks: <strong className="text-hg-text">{draft.clicks}</strong> (people — link-preview crawlers are not counted)
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap text-[10px] text-hg-text-dim font-sans uppercase tracking-wider">
        {(draft.services || []).map((s) => (
          <span key={s} className="border border-hg-border rounded-sm px-1.5 py-0.5">{s}</span>
        ))}
      </div>

      {/* The drafter's note to the approver - e.g. what may be added once the store sells, and
          that it has to be added BEFORE approving, because the digest covers the copy. */}
      {draft.note && (
        <p data-testid={`draft-note-${draft.id}`} className="text-xs text-hg-text-dim font-sans border-l-2 border-hg-border pl-3">
          {draft.note}
        </p>
      )}

      {/* --- everything he should know BEFORE he says yes --- */}
      {(draft.warnings || []).length > 0 && (
        <ul data-testid={`draft-warnings-${draft.id}`} className="space-y-1">
          {draft.warnings.map((w, i) => (
            <li key={i} className="flex gap-2 items-start text-xs text-amber-300 font-sans">
              <AlertTriangle size={13} strokeWidth={1.5} className="shrink-0 mt-0.5" />
              <span>{w}</span>
            </li>
          ))}
        </ul>
      )}

      {/* --- an approval that no longer matches is not an approval --- */}
      {approval && !approval.stale && (
        <div className="text-xs text-hg-green font-sans flex items-center gap-1.5">
          <Check size={13} strokeWidth={2} />
          Approved by {approval.approved_by}
          {approval.approved_at ? ` on ${new Date(approval.approved_at).toLocaleDateString()}` : ''}
        </div>
      )}
      {approval && approval.stale && (
        <div data-testid={`draft-stale-${draft.id}`} className="flex gap-2 items-start border border-red-400 rounded-sm p-3 text-xs text-red-300 font-sans">
          <RefreshCw size={13} strokeWidth={1.5} className="shrink-0 mt-0.5" />
          <span>This post has changed since it was approved, so that approval is void. Read it again and re-approve.</span>
        </div>
      )}

      {(draft.split_from || (draft.split_to || []).length > 0) && (
        <div data-testid={`draft-split-${draft.id}`} className="text-xs text-hg-text-dim font-sans border-l-2 border-hg-border pl-3">
          {draft.split_from
            ? `Its own post on ${(draft.services || []).join(', ')} — split from ${draft.split_from} when it was scheduled.`
            : `Also posting as its own post: ${draft.split_to.join(', ')}.`}
        </div>
      )}

      {draft.skipped && (
        <div data-testid={`draft-skipped-${draft.id}`} className="text-xs text-hg-text-dim font-sans border-l-2 border-hg-border pl-3">
          Skipped by {draft.skipped.by}: {draft.skipped.reason}
        </div>
      )}

      {draft.returned && (
        <div className="text-xs text-hg-text-dim font-sans border-l-2 border-hg-border pl-3">
          Sent back by {draft.returned.by}: “{draft.returned.note}”
        </div>
      )}

      {/* --- actions --- */}
      <div className="pt-1 border-t border-hg-border space-y-3">
        {!isAd && !draft.can_approve && draft.approve_block && (
          <p className="text-xs text-hg-text-dim font-sans pt-3">{draft.approve_block}</p>
        )}
        <div className="flex gap-2 flex-wrap pt-3">
          {!isAd && draft.one_click_block === null && !img.preview_error && (
            <button
              data-testid={`draft-oneclick-${draft.id}`}
              disabled={busy}
              onClick={() => setOneClickOpen((v) => !v)}
              className="flex items-center gap-1.5 bg-hg-green text-hg-bg text-xs font-sans uppercase tracking-wider px-4 py-2 rounded-sm disabled:opacity-50"
            >
              <CalendarClock size={13} strokeWidth={2} />
              {draft.state === 'approved' ? 'Schedule to Buffer' : 'Approve and schedule to Buffer'}
            </button>
          )}
          {!isAd && draft.state === 'approved' && (
            <button
              data-testid={`draft-schedule-${draft.id}`}
              disabled={busy}
              onClick={() => setSchedOpen((v) => !v)}
              className="flex items-center gap-1.5 bg-hg-green text-hg-bg text-xs font-sans uppercase tracking-wider px-4 py-2 rounded-sm disabled:opacity-50"
            >
              <CalendarClock size={13} strokeWidth={2} />
              Schedule
            </button>
          )}
          {draft.state === 'scheduled' && (
            <button
              data-testid={`draft-cancel-${draft.id}`}
              disabled={busy}
              onClick={() => onScheduled(draft, 'cancel')}
              className="flex items-center gap-1.5 border border-hg-border text-hg-text-dim hover:text-hg-text text-xs font-sans uppercase tracking-wider px-4 py-2 rounded-sm disabled:opacity-50"
            >
              <Ban size={13} strokeWidth={1.5} />
              Cancel scheduled post
            </button>
          )}
          {!isAd && draft.can_approve && !img.preview_error && (
            <button
              data-testid={`draft-approve-${draft.id}`}
              disabled={busy}
              onClick={() => onApprove(draft)}
              className="flex items-center gap-1.5 bg-hg-green text-hg-bg text-xs font-sans uppercase tracking-wider px-4 py-2 rounded-sm disabled:opacity-50"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} strokeWidth={2} />}
              {approval && approval.stale ? 'Re-approve' : 'Approve'}
            </button>
          )}
          <button
            data-testid={`draft-attach-${draft.id}`}
            disabled={busy}
            onClick={() => setAttachOpen((v) => !v)}
            className="flex items-center gap-1.5 border border-hg-border text-hg-text-dim hover:text-hg-text text-xs font-sans uppercase tracking-wider px-4 py-2 rounded-sm disabled:opacity-50"
          >
            <Paperclip size={13} strokeWidth={1.5} />
            {draft.image?.has_asset ? 'Replace artwork' : 'Attach artwork'}
          </button>
          <button
            data-testid={`draft-sendback-${draft.id}`}
            disabled={busy}
            onClick={() => setNoteOpen((v) => !v)}
            className="flex items-center gap-1.5 border border-hg-border text-hg-text-dim hover:text-hg-text text-xs font-sans uppercase tracking-wider px-4 py-2 rounded-sm disabled:opacity-50"
          >
            <Undo2 size={13} strokeWidth={1.5} />
            Send back
          </button>
        </div>
        {oneClickOpen && (
          <OneClickPanel draft={draft} onClose={() => setOneClickOpen(false)}
                         onDone={(res) => { setOneClickOpen(false); onOneClick(res); }} />
        )}
        {schedOpen && (
          <SchedulePanel draft={draft} onClose={() => setSchedOpen(false)}
                         onScheduled={(card) => { setSchedOpen(false); onScheduled(card); }} />
        )}
        {attachOpen && (
          <AttachPanel draft={draft} onClose={() => setAttachOpen(false)}
                       onAttached={(card) => { setAttachOpen(false); onAttached(card); }} />
        )}
        {noteOpen && (
          <div className="space-y-2">
            <textarea
              data-testid={`draft-note-${draft.id}`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="What should change? This is recorded on the draft."
              className="w-full bg-hg-bg border border-hg-border rounded-sm p-2 text-sm text-hg-text font-sans placeholder-hg-text-dim focus:outline-none focus:ring-1 focus:ring-hg-green resize-none"
            />
            <button
              data-testid={`draft-note-submit-${draft.id}`}
              disabled={busy || !note.trim()}
              onClick={() => { onReturn(draft, note.trim()); setNote(''); setNoteOpen(false); }}
              className="text-xs font-sans uppercase tracking-wider border border-hg-border text-hg-text px-4 py-2 rounded-sm disabled:opacity-40"
            >
              Send it back
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function SocialDrafts() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');
  // `?module=social&tab=drafts&draft=<id>` opens on that one draft: the list shows everything
  // (a deep-linked draft may not be "waiting"), and the card is scrolled to and outlined.
  const focusId = useMemo(() => {
    try { return new URLSearchParams(window.location.search).get('draft') || ''; } catch { return ''; }
  }, []);
  const [filter, setFilter] = useState(() => (focusId ? 'all' : 'waiting'));

  useEffect(() => {
    if (!focusId || loading) return;
    const el = document.getElementById(`draft-${focusId}`);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focusId, loading, drafts]);

  const load = useCallback(async (state) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/social/drafts`, { params: { state } });
      setDrafts(res.data.drafts || []);
    } catch (e) {
      // A failed read is not an empty queue. Saying "no drafts" here would tell him the
      // backlog is clear when we simply could not ask.
      setError(e?.response?.data?.detail || 'Could not load the drafts. This is a failed request, not an empty queue — try again.');
      setDrafts([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(filter); }, [load, filter]);

  const replace = (card) => setDrafts((ds) => ds.map((d) => (d.id === card.id ? card : d)));

  const handleApprove = async (draft) => {
    setBusyId(draft.id);
    setError('');
    try {
      const res = await axios.post(`${API}/social/drafts/${draft.id}/approve`, {});
      replace(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not approve that post.');
    }
    setBusyId('');
  };

  // Scheduling replaces the card; cancelling is a call of its own. Both come back through
  // one handler so the list always shows what the server says, never what the click implied.
  const handleScheduled = async (cardOrDraft, action) => {
    if (action !== 'cancel') {
      replace(cardOrDraft);
      // It is no longer waiting on him — it is in Buffer's queue.
      if (filter === 'waiting') setDrafts((ds) => ds.filter((d) => d.id !== cardOrDraft.id));
      return;
    }
    setBusyId(cardOrDraft.id);
    setError('');
    try {
      const res = await axios.post(`${API}/social/drafts/${cardOrDraft.id}/unschedule`);
      replace(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not cancel that scheduled post.');
    }
    setBusyId('');
  };

  // The one click comes back with every row it touched (a Facebook + X draft is two rows now)
  // and a result per channel. Scheduled rows leave the "waiting" list; a channel Buffer refused
  // is said in the notice, never dropped.
  const [notice, setNotice] = useState(null);
  const handleOneClick = (res) => {
    const cards = res.drafts || [];
    setDrafts((ds) => {
      const byId = Object.fromEntries(cards.map((c) => [c.id, c]));
      let next = ds.map((d) => byId[d.id] || d);
      cards.forEach((c) => { if (!next.some((d) => d.id === c.id)) next.push(c); });
      if (filter === 'waiting') next = next.filter((d) => ['needs_review', 'needs_asset', 'approved'].includes(d.state));
      return next;
    });
    setNotice({ results: res.results || [], when: res.local?.display || '' });
  };

  const handleReturn = async (draft, note) => {
    setBusyId(draft.id);
    setError('');
    try {
      const res = await axios.post(`${API}/social/drafts/${draft.id}/return`, { note });
      replace(res.data);
      if (filter === 'waiting') setDrafts((ds) => ds.filter((d) => d.id !== draft.id));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not send that post back.');
    }
    setBusyId('');
  };

  return (
    <div className="space-y-4" data-testid="social-drafts-tab">
      {/* Approving is not scheduling, and the screen says so rather than leaving him to
          infer it from a green button. */}
      <div className="border border-hg-border rounded-sm p-3 text-xs text-hg-text-dim font-sans">
        Approving records your OK against this exact copy, image, channel and time.
        <strong className="text-hg-text"> It does not schedule or post anything.</strong> If the post
        changes afterwards the approval is void and you will be asked again.
        {' '}<strong className="text-hg-text">Approve and schedule to Buffer</strong> does both, for the
        draft’s date — after showing you exactly what goes out on each channel.
      </div>

      {notice && (
        <div data-testid="oneclick-notice" className="border border-hg-green rounded-sm p-3 text-xs font-sans space-y-1">
          {notice.results.map((r) => (
            <div key={r.draft_id} className={r.ok ? 'text-hg-green' : 'text-red-300'}>
              {r.ok ? `Scheduled to ${r.channel}${notice.when ? ` for ${notice.when}` : ''} (${r.draft_id}).`
                    : `${r.channel}: not scheduled — ${r.error}`}
            </div>
          ))}
          <button onClick={() => setNotice(null)} className="text-hg-text-dim hover:text-hg-text">Dismiss</button>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {[['waiting', 'Waiting on you'], ['needs_review', 'Needs review'], ['needs_asset', 'Needs an image'], ['approved', 'Approved'], ['scheduled', 'Scheduled'], ['skipped', 'Skipped'], ['all', 'All']].map(([v, label]) => (
          <button
            key={v}
            data-testid={`drafts-filter-${v}`}
            onClick={() => setFilter(v)}
            className={`text-xs font-sans px-3 py-1.5 rounded-sm border ${filter === v ? 'border-hg-green text-hg-green' : 'border-hg-border text-hg-text-dim hover:text-hg-text'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div data-testid="drafts-error" className="border border-red-400 rounded-sm p-3 text-xs text-red-300 font-sans">{error}</div>
      )}

      {loading && <div className="text-center py-8 text-hg-text-dim text-sm font-sans">Loading drafts...</div>}

      {!loading && !error && drafts.length === 0 && (
        <div className="text-center py-12 text-hg-text-dim text-sm font-sans">Nothing waiting on you here.</div>
      )}

      <div className="space-y-4">
        {drafts.map((d) => (
          <DraftCard key={d.id} draft={d} busy={busyId === d.id} focused={d.id === focusId}
                     onApprove={handleApprove} onReturn={handleReturn} onAttached={replace}
                     onScheduled={handleScheduled} onOneClick={handleOneClick} />
        ))}
      </div>
    </div>
  );
}
